import { z } from 'zod';
import { defineCollection, type RecordOf } from '../collection.ts';
import { id, isoDate, isoDateTime, longText, provenance, relatedRefs, requiredText, shortText } from '../fields.ts';
import { recurrenceSchema } from '../recurrence.ts';

export const EVENT_CATEGORIES = ['event', 'birthday', 'appointment', 'holiday', 'reminder'] as const;

/**
 * Timed events store instants (startsAt/endsAt, ISO with offset).
 * All-day events store calendar dates (startDate/endDate, end inclusive) so
 * they don't drift across time zones.
 */
export const eventSchema = z
  .object({
    title: requiredText(200),
    notes: longText(10_000).default(''),
    location: shortText(300).default(''),
    allDay: z.boolean().default(false),
    startsAt: isoDateTime.nullable().default(null),
    endsAt: isoDateTime.nullable().default(null),
    startDate: isoDate.nullable().default(null),
    endDate: isoDate.nullable().default(null),
    /** IANA zone of the creator, for display hints. */
    timezone: shortText(64).default('UTC'),
    recurrence: recurrenceSchema.nullable().default(null),
    /** Occurrence dates removed from a recurring series. */
    exdates: z.array(isoDate).max(500).default([]),
    /** Set on a single occurrence detached from its series for separate editing. */
    seriesId: id.nullable().default(null),
    originalDate: isoDate.nullable().default(null),
    participants: z.array(id).max(50).default([]),
    /** Minutes before start. */
    reminders: z.array(z.number().int().min(0).max(40_320)).max(5).default([]),
    category: z.enum(EVENT_CATEGORIES).default('event'),
    related: relatedRefs.default([]),
    source: provenance.nullable().default(null),
  })
  .refine((e) => (e.allDay ? e.startDate !== null && e.endDate !== null : e.startsAt !== null && e.endsAt !== null), {
    message: 'Start and end are required',
  })
  .refine((e) => (e.allDay ? (e.endDate ?? '') >= (e.startDate ?? '') : Date.parse(e.endsAt ?? '') >= Date.parse(e.startsAt ?? '')), {
    message: 'End must be after start',
  });

export const events = defineCollection({
  name: 'events',
  module: 'calendar',
  label: { one: 'event', other: 'events' },
  schema: eventSchema,
  defaultAudience: 'family',
  childAccess: 'read',
  editPolicy: 'collaborative',
  // `source` stays writable so a calendar refresh can update its provenance timestamp.
  immutableFields: ['seriesId'],
  manualMergeFields: ['notes'],
});

export type CalendarEvent = RecordOf<typeof events>;
